// src/lib/supabase.ts - Auth-only client (NO DATABASE ACCESS)
import { createClient } from '@supabase/supabase-js';

let supabaseInstance: ReturnType<typeof createClient> | null = null;
let initializationPromise: Promise<ReturnType<typeof createClient>> | null = null;

export const initSupabase = async () => {
  // Return existing initialization promise if one is in progress
  if (initializationPromise) return initializationPromise;
  
  // Return existing instance if already initialized
  if (supabaseInstance) return Promise.resolve(supabaseInstance);
  
  initializationPromise = (async () => {
    try {
      // Always use relative API path - works for both dev and prod
      const apiUrl = '/api';
      
      console.log('Fetching Supabase config from:', `${apiUrl}/supabase-config`);
      
      const response = await fetch(`${apiUrl}/supabase-config`);
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const responseText = await response.text();
        console.log('Error response body:', responseText.substring(0, 200));
        throw new Error(`Failed to fetch Supabase config: ${response.status} ${response.statusText}`);
      }
    
      const responseText = await response.text();
      console.log('Response body preview:', responseText.substring(0, 100));
      
      let config;
      try {
        config = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error. Full response:', responseText);
        throw new Error(`Invalid JSON response: ${parseError.message}`);
      }
      console.log('Received config:', { 
        hasUrl: !!config.url, 
        hasKey: !!config.key 
      });
    
      if (!config.url || !config.key) {
        throw new Error('Invalid Supabase configuration received: missing url or key');
      }

      supabaseInstance = createClient(config.url, config.key);
      console.log('Supabase client created with URL:', config.url.substring(0, 20) + '...');
      
      // Test the connection
      const { error: testError } = await supabaseInstance.auth.getSession();
      if (testError) {
        console.error('Auth session test failed:', testError);
        throw new Error(`Failed to connect to Supabase: ${testError.message}`);
      }
      
      console.log('✅ Supabase client initialized successfully');
      return supabaseInstance;
    } catch (error) {
      console.error('❌ Error initializing Supabase:', error);
      supabaseInstance = null;
      throw error;
    } finally {
      initializationPromise = null;
    }
  })();
  
  return initializationPromise;
};

export const getSupabase = () => {
  if (!supabaseInstance) {
    console.error('❌ getSupabase() called but client not initialized');
    throw new Error('Supabase client not initialized. Please ensure initSupabase() is called first.');
  }
  console.log('✅ getSupabase() returning initialized client');
  return supabaseInstance;
};

// Helper function to test Supabase connection
export const testSupabaseConnection = async () => {
  try {
    const supabase = await initSupabase();
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }
    
    console.log('✅ Supabase connection test successful');
    return true;
  } catch (error) {
    console.error('❌ Supabase connection test failed:', error);
    return false;
  }
};